// Bhavishyavani cosmic zodiac wheel — v2.
// Enhancements over v1:
//   • 12 zodiac glyphs on the outer ring (muted gold)
//   • Devanagari planet-name ring inside (शुक्र/बुध/मंगल…)
//   • 7 shaded spherical planets on 3 orbital arcs (SVG radial gradients)
//   • Central golden sun with 24-ray burst

const ZODIAC_GLYPHS = [
  "\u2648\uFE0E", "\u2649\uFE0E", "\u264A\uFE0E", "\u264B\uFE0E",
  "\u264C\uFE0E", "\u264D\uFE0E", "\u264E\uFE0E", "\u264F\uFE0E",
  "\u2650\uFE0E", "\u2651\uFE0E", "\u2652\uFE0E", "\u2653\uFE0E",
];

// Devanagari planet names — repeated 12x with subtle rotation feel
const DEVANAGARI_RING = [
  "सूर्य", "चन्द्र", "मङ्गल", "बुध", "गुरु", "शुक्र",
  "शनि", "राहु", "केतु", "सूर्य", "चन्द्र", "गुरु",
];

// Deterministic decorative planet placements (angle in degrees, orbit ring 1/2/3, size, palette)
const PLANETS = [
  { name: "Jupiter", ring: 3, angle: -35, size: 14, palette: ["#F4C58B","#B57D3F","#5C3A17"], glow: true  },
  { name: "Saturn",  ring: 3, angle: 175, size: 12, palette: ["#EAD59D","#B58E4F","#4E3A15"], rings: true },
  { name: "Mars",    ring: 2, angle: -130, size: 8, palette: ["#EF9E7B","#B24A2A","#4B1C0E"] },
  { name: "Venus",   ring: 2, angle:   62, size: 8, palette: ["#F3D9A4","#C5924C","#5A3C16"], glow: true  },
  { name: "Mercury", ring: 1, angle:   35, size: 6, palette: ["#D9CAA6","#8A7148","#3F311A"] },
  { name: "Moon",    ring: 3, angle:   75, size: 10,palette: ["#EFEFE9","#B3AFA3","#4B4A46"], glow: true  },
  { name: "Neptune", ring: 3, angle: -115, size: 9, palette: ["#A8C9E6","#4E7AA8","#1B324C"] },
];

export default function ZodiacWheel({ size = 620, className = "" }) {
  const cx = 250, cy = 250, R = 240;
  const rings = [R * 0.44, R * 0.60, R * 0.76];
  const glyphRadius = R * 0.94;
  const sanskritRadius = R * 0.36;   // inner Devanagari ring
  const centerR = 42;

  return (
    <svg viewBox="0 0 500 500"
         className={className}
         width="100%" height="100%"
         style={{ maxWidth: size, maxHeight: size, display: "block" }}
         role="img" aria-label="Bhavishyavani cosmic zodiac wheel"
         data-testid="hero-zodiac-wheel">
      <defs>
        <radialGradient id="sun-core" cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="#FFF3CE" stopOpacity="1" />
          <stop offset="45%"  stopColor="#DCCB9A" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#7B5A24" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="sun-halo" cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="#DCCB9A" stopOpacity="0.4" />
          <stop offset="70%"  stopColor="#C6A15B" stopOpacity="0.05" />
          <stop offset="100%" stopColor="#0B1020" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="ring-stroke" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor="#DCCB9A" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#C6A15B" stopOpacity="0.35" />
        </linearGradient>

        {/* Text path for Sanskrit ring */}
        <path id="sanskrit-path"
              d={`M ${cx - sanskritRadius},${cy} a ${sanskritRadius},${sanskritRadius} 0 1,1 ${sanskritRadius * 2},0 a ${sanskritRadius},${sanskritRadius} 0 1,1 ${-sanskritRadius * 2},0`}
              fill="none" />

        {/* Radial gradients for each planet colour palette */}
        {PLANETS.map((p, i) => (
          <radialGradient key={p.name} id={`planet-${i}`} cx="35%" cy="30%" r="70%">
            <stop offset="0%"   stopColor={p.palette[0]} />
            <stop offset="55%"  stopColor={p.palette[1]} />
            <stop offset="100%" stopColor={p.palette[2]} />
          </radialGradient>
        ))}

        {/* Subtle glow filter */}
        <filter id="planet-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>

        <symbol id="sun-rays" viewBox="-40 -40 80 80">
          {Array.from({ length: 24 }).map((_, i) => {
            const a = (i * 360) / 24;
            return (
              <line key={i} x1="0" y1="-14" x2="0" y2="-34"
                    stroke="#DCCB9A" strokeWidth="0.7" strokeLinecap="round"
                    transform={`rotate(${a})`} opacity={i % 2 ? 0.55 : 0.9} />
            );
          })}
        </symbol>
      </defs>

      {/* Outer diffuse halo */}
      <circle cx={cx} cy={cy} r={R * 1.05} fill="url(#sun-halo)" />

      {/* Orbital rings — thin, dashed */}
      {rings.map((r, i) => (
        <circle key={i} cx={cx} cy={cy} r={r}
          fill="none" stroke="url(#ring-stroke)"
          strokeWidth={i === 1 ? 0.9 : 0.6}
          strokeDasharray={i === 1 ? "" : "1 4"} opacity={0.6} />
      ))}

      {/* Outer + inner double zodiac ring */}
      <circle cx={cx} cy={cy} r={R * 0.88} fill="none"
              stroke="#C6A15B" strokeWidth="0.6" opacity="0.7" />
      <circle cx={cx} cy={cy} r={R} fill="none"
              stroke="#C6A15B" strokeWidth="0.5" opacity="0.35" />

      {/* 12 zodiac division ticks */}
      {ZODIAC_GLYPHS.map((_, i) => {
        const a = (i * 30 - 90) * (Math.PI / 180);
        const x1 = cx + Math.cos(a) * R * 0.88;
        const y1 = cy + Math.sin(a) * R * 0.88;
        const x2 = cx + Math.cos(a) * R;
        const y2 = cy + Math.sin(a) * R;
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
                     stroke="#C6A15B" strokeWidth="0.5" opacity="0.5" />;
      })}

      {/* Zodiac glyphs */}
      {ZODIAC_GLYPHS.map((g, i) => {
        const a = ((i * 30) + 15 - 90) * (Math.PI / 180);
        const x = cx + Math.cos(a) * glyphRadius;
        const y = cy + Math.sin(a) * glyphRadius;
        return (
          <text key={i} x={x} y={y + 5} textAnchor="middle"
            style={{
              fontSize: 16, fill: "#DCCB9A",
              fontFamily: "'Cormorant Garamond','Times New Roman',serif",
              fontWeight: 500, fontVariantEmoji: "text",
            }}>
            {g}
          </text>
        );
      })}

      {/* Devanagari planet-name inner ring (text on circular path) */}
      <text style={{ fontSize: 11, fill: "#DCCB9A", opacity: 0.6,
                     fontFamily: "'Noto Serif Devanagari','Noto Sans Devanagari',serif" }}>
        <textPath href="#sanskrit-path" startOffset="0">
          {DEVANAGARI_RING.map((w) => `${w}   ✦   `).join("")}
        </textPath>
      </text>
      <circle cx={cx} cy={cy} r={sanskritRadius + 10} fill="none"
              stroke="#C6A15B" strokeWidth="0.4" opacity="0.35" />
      <circle cx={cx} cy={cy} r={sanskritRadius - 6} fill="none"
              stroke="#C6A15B" strokeWidth="0.4" opacity="0.25" />

      {/* Planet spheres */}
      {PLANETS.map((p, i) => {
        const r = rings[p.ring - 1];
        const a = (p.angle - 90) * (Math.PI / 180);
        const x = cx + Math.cos(a) * r;
        const y = cy + Math.sin(a) * r;
        return (
          <g key={p.name} filter={p.glow ? "url(#planet-glow)" : undefined}>
            {/* soft halo */}
            <circle cx={x} cy={y} r={p.size + 3}
                    fill={p.palette[0]} opacity="0.14" />
            {/* body */}
            <circle cx={x} cy={y} r={p.size}
                    fill={`url(#planet-${i})`}
                    stroke={p.palette[2]} strokeWidth="0.6" />
            {/* highlight glint */}
            <ellipse cx={x - p.size * 0.35} cy={y - p.size * 0.4}
                     rx={p.size * 0.3} ry={p.size * 0.18}
                     fill="#FFFFFF" opacity="0.35" />
            {/* Saturn's ring */}
            {p.rings && (
              <g transform={`rotate(-18 ${x} ${y})`}>
                <ellipse cx={x} cy={y} rx={p.size * 1.9} ry={p.size * 0.45}
                         fill="none" stroke={p.palette[1]} strokeWidth="1.2"
                         opacity="0.85" />
                <ellipse cx={x} cy={y} rx={p.size * 2.3} ry={p.size * 0.55}
                         fill="none" stroke={p.palette[1]} strokeWidth="0.6"
                         opacity="0.45" />
              </g>
            )}
          </g>
        );
      })}

      {/* Central sun */}
      <g>
        <use href="#sun-rays" x={cx - 40} y={cy - 40} width="80" height="80" opacity="0.9" />
        <circle cx={cx} cy={cy} r={centerR} fill="url(#sun-core)" />
        <circle cx={cx} cy={cy} r="11" fill="#FFF3CE" opacity="0.95" />
      </g>

      {/* Scattered background stars */}
      {[
        [60,90],[120,40],[420,70],[470,210],[30,260],[80,400],
        [420,420],[470,380],[200,470],[370,460],[110,200],[400,160],
        [280,30],[230,40],[490,140],[10,140],[350,20],[150,485],
      ].map(([x,y], i) => (
        <circle key={i} cx={x} cy={y} r={i%3?0.8:1.4}
                fill="#DCCB9A" opacity={i%2?0.45:0.85} />
      ))}
    </svg>
  );
}
